import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { AccessInfo } from '../accessInfoTypes';

interface ProjectSecurityContextProps {
  permissions?: AccessInfo;
}

interface ProjectSecurityContextData {
  permissions?: AccessInfo;
  isAccessGranted: (section: string, permission: string) => boolean;
}

const ProjectSecurityDataContext = createContext<Nullable<ProjectSecurityContextData>>(null);

export const useProjectSecurityContext = () => {
  const context = useContext(ProjectSecurityDataContext);
  if (!context) {
    throw new Error('useProjectSecurityContext must be used within ProjectSecurityContext');
  }
  return context;
};

export const ProjectSecurityContext = ({ permissions, children }: PropsWithChildren<ProjectSecurityContextProps>) => {
  const contextValue = useMemo(() => {
    if (!permissions) {
      return {
        isAccessGranted: (section: string, permission: string): boolean => false,
      };
    }

    const projectPermissions =
      permissions?.sections.flatMap(({ section, allowedActions }) =>
        allowedActions.map(permission => `GRAPHQL__${section}__${permission}`),
      ) || [];

    const isAccessGranted = (section: string, permission: string) => {
      return projectPermissions.includes(`GRAPHQL__${section}__${permission}`);
    };

    return {
      permissions: permissions,
      isAccessGranted,
    };
  }, [permissions]);

  return <ProjectSecurityDataContext.Provider value={contextValue}>{children}</ProjectSecurityDataContext.Provider>;
};
